/**
 * Cart discount component – applies/removes discount codes.
 * Uses /cart.js as source of truth: apply via cart/update.js, then fetch /cart.js and re-render.
 */
class CartDiscountComponent extends HTMLElement {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
  }

  connectedCallback() {
    const form = this.querySelector('[data-discount-form]');
    if (form) form.addEventListener('submit', this.applyDiscount.bind(this));
    this.addEventListener('click', this.handleClick.bind(this));

    const details = this.querySelector('details');
    if (details) {
      details.addEventListener('toggle', this.handleDiscountToggle.bind(this));
    }
    this.cartDrawer?.addEventListener('click', this.handleCrossSellExpand.bind(this));
  }

  handleDiscountToggle() {
    const details = this.querySelector('details');
    const crossSell = document.querySelector('[data-cross-sell]');
    if (!crossSell) return;
    if (details?.open) {
      crossSell.classList.add('wt-cart__cross-sell--collapsed');
      crossSell.querySelector('[data-cross-sell-expand]')?.setAttribute('aria-expanded', 'false');
    } else {
      crossSell.classList.remove('wt-cart__cross-sell--collapsed');
      crossSell.querySelector('[data-cross-sell-expand]')?.setAttribute('aria-expanded', 'true');
    }
  }

  handleCrossSellExpand(e) {
    const trigger = e.target.closest('[data-cross-sell-expand]');
    if (!trigger) return;
    e.preventDefault();
    const crossSell = document.querySelector('[data-cross-sell]');
    if (crossSell) {
      crossSell.classList.remove('wt-cart__cross-sell--collapsed');
      trigger.setAttribute('aria-expanded', 'true');
    }
  }

  handleClick(e) {
    const pill = e.target.closest('[data-discount-remove]');
    if (pill) {
      e.preventDefault();
      this.removeDiscount(e);
    }
  }

  getExistingDiscounts() {
    const pills = this.querySelectorAll('.cart-discount__pill');
    return Array.from(pills)
      .map((el) => el.dataset.discountCode)
      .filter(Boolean);
  }

  /** Format cents to money string (e.g. £10.00). Shopify uses cents (int); handles decimal strings. */
  formatMoney(cents, currency = 'GBP') {
    if (cents == null || cents === '') return '';
    const num = typeof cents === 'string' ? parseFloat(cents) : Number(cents);
    if (isNaN(num)) return '';
    const value = typeof cents === 'string' && String(cents).includes('.') ? num : num / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(value);
    } catch {
      return value.toFixed(2);
    }
  }

  showError(message) {
    const errorEl = this.querySelector('[data-discount-error]');
    const textEl = this.querySelector('[data-discount-error-text]');
    if (errorEl) errorEl.classList.remove('hidden');
    if (textEl) textEl.textContent = message || (window.cartStrings?.discountError || 'This discount code is not valid.');
  }

  hideError() {
    const errorEl = this.querySelector('[data-discount-error]');
    if (errorEl) errorEl.classList.add('hidden');
  }

  /**
   * Refresh drawer UI from cart JSON (from /cart.js).
   * Updates: discount pills, footer discounts list, subtotal, line item prices, icon bubble count.
   * @param {Object} cart - Cart from /cart.js or cart/update.js response
   * @param {{ appliedCode?: string }} [opts] - Optional: appliedCode when just applied (fallback when API omits code)
   */
  refreshFromCart(cart, opts = {}) {
    const currency = cart.currency || 'GBP';
    const appliedCode = opts.appliedCode?.trim();
    const codes = cart.discount_applications || cart.cart_level_discount_applications || [];
    let discountCodes = codes
      .filter((d) => d)
      .map((d) => {
        let code = (d.title ?? d.code ?? d.key ?? d.value ?? (d.discount && d.discount.code) ?? '').toString().trim();
        if (!code) code = appliedCode || 'Discount';
        const isRemovable = !d.type || d.type === 'discount_code' || d.type === 'DiscountCode';
        return {
          code: code,
          amount: d.total_allocated_amount ?? d.amount ?? d.value ?? 0,
          removable: isRemovable,
        };
      });

    if (discountCodes.length === 0 && cart.items) {
      const byCode = new Map();
      for (const item of cart.items) {
        const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
        for (const a of allocations) {
          const app = a.discount_application || a;
          let code = (a.code ?? app.code ?? app.title ?? a.title ?? app.key ?? app.value ?? (app.discount && app.discount.code) ?? '').toString().trim();
          if (!code) code = appliedCode || 'Discount';
          const amount = a.amount ?? a.total_allocated_amount ?? a.discounted_amount?.amount ?? 0;
          const existing = byCode.get(code) || { code, amount: 0, removable: true };
          existing.amount += Number(amount) || 0;
          byCode.set(code, existing);
        }
      }
      discountCodes = Array.from(byCode.values()).filter((d) => d.amount > 0 || d.code !== 'Discount');
    }

    if (appliedCode) {
      discountCodes = discountCodes.map((d) =>
        d.code === 'Discount' ? { ...d, code: appliedCode } : d
      );
    }

    // Update discount pills
    const codesList = this.querySelector('.cart-discount__codes');
    const closeIcon = document.getElementById('cart-discount-close-icon');
    const closeSvg = closeIcon ? closeIcon.innerHTML : '';

    const appliedLabel = this.escapeHtml((window.cartStrings?.discountApplied || 'Discount applied'));
    const removeLabel = this.escapeHtml((window.cartStrings?.discountRemove || 'Remove'));
    const discountIcon = document.getElementById('cart-discount-icon');
    const discountSvg = discountIcon ? discountIcon.innerHTML : '';

    if (codesList) {
      codesList.innerHTML = discountCodes
        .map(
          (d) => `
        <li class="cart-discount__pill" data-discount-code="${this.escapeHtml(d.code)}" data-discount-pill
          data-discount-removable="${d.removable}"
          aria-label="${appliedLabel}: ${this.escapeHtml(d.code)}">
          <span class="cart-discount__pill-label">
            <span class="cart-discount__pill-icon">${discountSvg}</span>
            <span class="cart-discount__pill-code">${this.escapeHtml(d.code)} (-${this.formatMoney(d.amount, currency)})</span>
          </span>
          ${d.removable ? `<button type="button" class="cart-discount__pill-remove" data-discount-remove
            aria-label="${removeLabel}: ${this.escapeHtml(d.code)}" tabindex="-1">${closeSvg}</button>` : ''}
        </li>
      `
        )
        .join('');
      this.querySelector('details')?.toggleAttribute('open', discountCodes.length > 0);
    }

    // Update cart icon bubble count
    const iconBubble = document.getElementById('cart-icon-bubble');
    const countSpan = iconBubble?.querySelector('.wt-header__panel__counter');
    const itemCount = cart.item_count || 0;

    if (iconBubble) {
      if (itemCount > 0) {
        if (countSpan) {
          countSpan.textContent = itemCount;
        } else {
          const span = document.createElement('span');
          span.className = 'wt-header__panel__counter';
          span.textContent = itemCount;
          iconBubble.insertBefore(span, iconBubble.querySelector('.wt-header__panel__label'));
        }
      } else if (countSpan) {
        countSpan.remove();
      }
    }

    // Update drawer header item count
    const drawerTitle = document.getElementById('CartDrawer-TotalQty');
    if (drawerTitle) {
      const cartTitle = (window.cartStrings && window.cartStrings.cartTitle) || 'Your cart';
      drawerTitle.textContent = itemCount > 0 ? `${cartTitle} (${itemCount})` : cartTitle;
    }

    // Update .wt-cart__subtotal__value with discounted total (and original when discounted)
    const subtotalEl = document.querySelector('.wt-cart .wt-cart__subtotal__value') || document.querySelector('[data-cart-subtotal]');
    if (subtotalEl && cart.items) {
      const originalTotal = cart.original_total_price ?? 0;
      const discountedTotal = cart.total_price ?? 0;
      const hasCartDiscount = originalTotal > discountedTotal && discountedTotal >= 0;

      if (hasCartDiscount) {
        subtotalEl.innerHTML = `<s class="wt-cart__subtotal__value--original">${this.formatMoney(originalTotal, currency)}</s><span class="wt-cart__subtotal__value--current">${this.formatMoney(discountedTotal, currency)}</span>`;
      } else if (discountCodes.length > 0 && cart.total_price != null) {
        subtotalEl.textContent = this.formatMoney(cart.total_price, currency);
      } else {
        let adjustedTotal = 0;
        for (const item of cart.items) {
          const preSalePrice = this._getPreSalePrice(item);
          if (preSalePrice > 0) {
            adjustedTotal += preSalePrice * (item.quantity || 1);
          } else {
            adjustedTotal += item.final_line_price ?? item.line_price ?? 0;
          }
        }
        subtotalEl.textContent = this.formatMoney(adjustedTotal, currency);
      }
    }

    // Update line item prices and item discount badges in cart drawer (removes when discount is removed)
    if (cart.items && cart.items.length > 0) {
      const regularPriceLabel = (window.cartStrings?.regularPrice) || 'Regular price';
      const salePriceLabel = (window.cartStrings?.salePrice) || 'Sale price';
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        const lineIndex = i + 1;
        const cartItemEl = document.getElementById(`CartDrawer-Item-${lineIndex}`);
        const wrapper = document.querySelector(`.wt-cart [data-cart-line-index="${lineIndex}"]`);
        if (!wrapper) continue;

        const preSalePrice = this._getPreSalePrice(item);
        const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
        const itemDiscountBadge = cartItemEl?.querySelector('.wt-cart__item-discount');
        const itemDiscountContainer = itemDiscountBadge?.parentElement;
        if (itemDiscountContainer) {
          if (allocations.length === 0) {
            itemDiscountContainer.remove();
          } else {
            const iconTemplate = document.getElementById('cart-discount-icon');
            itemDiscountContainer.replaceChildren(
              ...allocations.map((a) => {
                const title = String(a.discount_application?.title ?? a.title ?? 'Discount');
                const amount = a.amount ?? a.total_allocated_amount ?? a.discounted_amount?.amount ?? 0;
                const div = document.createElement('div');
                div.className = 'wt-cart__item-discount';
                if (iconTemplate?.content) div.appendChild(iconTemplate.content.cloneNode(true));
                div.appendChild(document.createTextNode(` ${title} - ${this.formatMoney(amount, currency)}`));
                return div;
              })
            );
          }
        }

        const qty = item.quantity || 1;
        const originalLine = item.original_line_price ?? item.line_price ?? 0;
        const finalLine = item.final_line_price ?? item.line_price ?? 0;
        const variant = item.variant || {};
        const compareAt = variant.compare_at_price ?? 0;
        const variantPrice = variant.price ?? 0;
        const hasCompareSale = compareAt > 0 && variantPrice < compareAt;

        let priceHtml;
        if (preSalePrice > 0) {
          priceHtml = `
            <div class="cart-item__discounted-prices">
              <span class="visually-hidden">${this.escapeHtml(regularPriceLabel)}</span>
              <s class="cart-item__old-price price price--end">${this.formatMoney(originalLine, currency)}</s>
              <span class="visually-hidden">${this.escapeHtml(salePriceLabel)}</span>
              <span class="price price--end" data-cart-line-total>${this.formatMoney(preSalePrice * qty, currency)}</span>
            </div>`;
        } else if (originalLine !== finalLine) {
          priceHtml = `
            <div class="cart-item__discounted-prices">
              <span class="visually-hidden">${this.escapeHtml(regularPriceLabel)}</span>
              <s class="cart-item__old-price price price--end">${this.formatMoney(originalLine, currency)}</s>
              <span class="visually-hidden">${this.escapeHtml(salePriceLabel)}</span>
              <span class="price price--end" data-cart-line-total>${this.formatMoney(finalLine, currency)}</span>
            </div>`;
        } else if (hasCompareSale) {
          priceHtml = `
            <div class="cart-item__discounted-prices">
              <span class="visually-hidden">${this.escapeHtml(regularPriceLabel)}</span>
              <s class="cart-item__old-price price price--end">${this.formatMoney(compareAt * qty, currency)}</s>
              <span class="visually-hidden">${this.escapeHtml(salePriceLabel)}</span>
              <span class="price price--end price--end-sale" data-cart-line-total>${this.formatMoney(variantPrice * qty, currency)}</span>
            </div>`;
        } else {
          priceHtml = `<span class="price price--end" data-cart-line-total>${this.formatMoney(originalLine, currency)}</span>`;
        }

        const loadingOverlay = wrapper.querySelector('.loading-overlay');
        const unitPrice = wrapper.querySelector('.unit-price');
        const parts = [];
        if (loadingOverlay) parts.push(loadingOverlay.outerHTML);
        parts.push(priceHtml);
        if (unitPrice) parts.push(unitPrice.outerHTML);
        wrapper.innerHTML = parts.join('');
      }
    }

    // Don't publish cartUpdate - it triggers cart-drawer-items to replace content with main-cart-items,
    // which overwrites our updates. Klarna/widgets can subscribe to cart changes separately if needed.
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  _getPreSalePrice(item) {
    if (!item.properties || typeof item.properties !== 'object') return 0;
    return parseInt(item.properties['_Pre-sale Price'] || item.properties['Pre-sale Price'], 10) || 0;
  }

  _discountWasApplied(cart, code) {
    const normalized = code.toLowerCase().trim();
    const match = (c) => (c || '').toString().trim().toLowerCase() === normalized;

    const cartCodes = cart.discount_applications || cart.cart_level_discount_applications || [];
    const checkD = (d) => match(d?.title ?? d?.code ?? d?.key ?? d?.value ?? d?.discount?.code);
    if (cartCodes.some(checkD)) return true;

    if (cart.items) {
      for (const item of cart.items) {
        const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
        if (allocations.some((a) => {
          const app = a.discount_application || a;
          return match(a?.code ?? app?.code ?? app?.title ?? a?.title ?? app?.key ?? app?.value ?? app?.discount?.code);
        })) return true;
      }
    }

    const orig = cart.original_total_price ?? 0;
    const total = cart.total_price ?? 0;
    if (orig > total) return true;

    return false;
  }

  async applyDiscount(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const input = form.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;

    const discountCodeValue = input.value.trim();
    if (!discountCodeValue) {
      this.showError(window.cartStrings?.discountEmpty || 'Please enter a discount code.');
      return;
    }

    const existingDiscounts = this.getExistingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    this.hideError();

    const discountParam = [...existingDiscounts, discountCodeValue].join(',');

    try {
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ discount: discountParam }),
      });

      const data = await response.json();

      if (data.status === 422) {
        input.value = '';
        this.showError(data.description || data.message || window.cartStrings?.discountError || 'This discount code is not valid.');
        this.querySelector('details')?.setAttribute('open', '');
        return;
      }

      let cart = Array.isArray(data.items) ? data : await fetch(`${routes.cart_url}.js`).then((r) => r.json());
      let wasApplied = this._discountWasApplied(cart, discountCodeValue);
      if (!wasApplied && Array.isArray(data.items)) {
        await new Promise((r) => setTimeout(r, 100));
        cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        wasApplied = this._discountWasApplied(cart, discountCodeValue);
      }
      if (!wasApplied) {
        this.showError(window.cartStrings?.discountError || 'This discount code is not valid.');
        this.querySelector('details')?.setAttribute('open', '');
        return;
      }
      input.value = '';
      this.refreshFromCart(cart, { appliedCode: discountCodeValue });
    } catch (error) {
      this.showError(window.cartStrings?.cart_error || 'There was an error updating your cart.');
      this.querySelector('details')?.setAttribute('open', '');
    }
  }

  async removeDiscount(event) {
    event.preventDefault();
    event.stopPropagation();

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.getExistingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    const updated = [...existingDiscounts];
    updated.splice(index, 1);
    const discountParam = updated.join(',');

    this.hideError();

    try {
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ discount: discountParam }),
      });

      const data = await response.json();

      if (data.status === 422) {
        this.showError(data.description || data.message || window.cartStrings?.discountError || 'This discount code is not valid.');
        return;
      }

      const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
      this.refreshFromCart(cart);
    } catch (error) {
      this.showError(window.cartStrings?.cart_error || 'There was an error updating your cart.');
    }
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscountComponent);
}
